import React, { useEffect, useRef } from 'react';

export default function MatrixBackground({ timeout = 100 }) {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const animationFrameRef = useRef(null);
  const yPositionsRef = useRef([]);
  const lastDrawTimeRef = useRef(0);
  const viewportSizeRef = useRef({ width: 0, height: 0 });
  const pointerRef = useRef({ x: 0, y: 0, targetX: 0, targetY: 0, active: false });

  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;

    if (!container || !canvas) {
      return undefined;
    }

    const ctx = canvas.getContext('2d');

    if (!ctx) {
      return undefined;
    }

    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const finePointerQuery = window.matchMedia('(hover: hover) and (pointer: fine)');

    const draw = (ctx, width, height) => {
      ctx.fillStyle = '#0002';
      ctx.fillRect(0, 0, width, height);
      ctx.font = '15pt monospace';

      const yPositions = yPositionsRef.current;
      const pointer = pointerRef.current;
      const columnWidth = 20;
      const columns = Math.floor(width / columnWidth);

      if (pointer.active) {
        if (reducedMotionQuery.matches) {
          pointer.x = pointer.targetX;
          pointer.y = pointer.targetY;
        } else {
          pointer.x += (pointer.targetX - pointer.x) * 0.28;
          pointer.y += (pointer.targetY - pointer.y) * 0.28;
        }

        const spotlight = ctx.createRadialGradient(
          pointer.x,
          pointer.y,
          0,
          pointer.x,
          pointer.y,
          190
        );
        spotlight.addColorStop(0, 'rgba(40, 255, 115, 0.1)');
        spotlight.addColorStop(0.5, 'rgba(20, 220, 90, 0.035)');
        spotlight.addColorStop(1, 'rgba(0, 0, 0, 0)');
        ctx.fillStyle = spotlight;
        ctx.fillRect(pointer.x - 190, pointer.y - 190, 380, 380);
      }

      for (let i = 0; i < columns; i++) {
        const text = String.fromCharCode(Math.random() * 128);
        const x = i * columnWidth;
        const y = yPositions[i] || 0;

        let glowStrength = 0;
        if (pointer.active) {
          const distance = Math.hypot(x - pointer.x, y - pointer.y);
          glowStrength = Math.max(0, 1 - distance / 190);
        }

        ctx.fillStyle = `rgba(${Math.round(25 + glowStrength * 155)}, 240, ${Math.round(
          25 + glowStrength * 95
        )}, ${0.5 + glowStrength * 0.5})`;
        ctx.shadowColor = `rgba(80, 255, 130, ${glowStrength * 0.95})`;
        ctx.shadowBlur = glowStrength * 22;

        ctx.fillText(text, x, y);
        yPositions[i] = y > height + Math.random() * 10000 ? 0 : y + 20;
      }

      ctx.shadowBlur = 0;
    };

    const animate = (timestamp) => {
      const { width, height } = viewportSizeRef.current;

      const lastTime = lastDrawTimeRef.current;
      if (width > 0 && height > 0 && timestamp - lastTime > timeout) {
        draw(ctx, width, height);
        lastDrawTimeRef.current = timestamp;
      }

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    const resizeCanvas = () => {
      const bounds = container.getBoundingClientRect();
      const viewportWidth = window.visualViewport?.width ?? window.innerWidth;
      const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
      const width = Math.max(Math.round(bounds.width), Math.round(viewportWidth), 1);
      const height = Math.max(Math.round(bounds.height), Math.round(viewportHeight), 1);
      const devicePixelRatio = window.devicePixelRatio || 1;

      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      canvas.width = Math.round(width * devicePixelRatio);
      canvas.height = Math.round(height * devicePixelRatio);
      ctx.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);
      viewportSizeRef.current = { width, height };

      const columns = Math.floor(width / 20);
      const previousPositions = yPositionsRef.current;
      yPositionsRef.current = Array.from(
        { length: columns },
        (_, index) => previousPositions[index] ?? Math.random() * height
      );
      lastDrawTimeRef.current = 0;
    };

    const resizeObserver =
      typeof ResizeObserver === 'function' ? new ResizeObserver(resizeCanvas) : null;

    const handlePointerMove = (event) => {
      if (!finePointerQuery.matches || event.pointerType === 'touch') {
        return;
      }

      const pointer = pointerRef.current;
      pointer.targetX = event.clientX;
      pointer.targetY = event.clientY;

      if (!pointer.active) {
        pointer.x = event.clientX;
        pointer.y = event.clientY;
        pointer.active = true;
      }
    };

    const deactivatePointer = () => {
      pointerRef.current.active = false;
    };

    resizeCanvas();
    resizeObserver?.observe(container);
    animationFrameRef.current = requestAnimationFrame(animate);
    window.addEventListener('resize', resizeCanvas);
    window.addEventListener('orientationchange', resizeCanvas);
    window.addEventListener('pointermove', handlePointerMove, { passive: true });
    window.addEventListener('blur', deactivatePointer);
    document.documentElement.addEventListener('mouseleave', deactivatePointer);
    window.visualViewport?.addEventListener('resize', resizeCanvas);

    return () => {
      cancelAnimationFrame(animationFrameRef.current);
      resizeObserver?.disconnect();
      window.removeEventListener('resize', resizeCanvas);
      window.removeEventListener('orientationchange', resizeCanvas);
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('blur', deactivatePointer);
      document.documentElement.removeEventListener('mouseleave', deactivatePointer);
      window.visualViewport?.removeEventListener('resize', resizeCanvas);
    };
  }, [timeout]);

  return (
    <div
      ref={containerRef}
      aria-hidden="true"
      style={{
        position: 'fixed',
        inset: 0,
        overflow: 'hidden',
        zIndex: -1,
        backgroundColor: 'black',
        pointerEvents: 'none',
      }}
    >
      <canvas ref={canvasRef} style={{ display: 'block', width: '100%', height: '100%' }} />
    </div>
  );
}
